/**
 * Routes.
 *
 * The language gate is the outermost thing: until a member has chosen,
 * screen 1 is the only screen. Non-negotiable 1 makes that choice the input
 * to everything after it, so there is nowhere sensible to render before it
 * exists.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { RequireAuth } from "./components/RequireAuth";
import { LOCALES, LOCALE_NAMES, setLocale, storedLocale, type Locale } from "./i18n";
import { api } from "./lib/api";
import { setVenueZone } from "./lib/format";
import Bookings from "./screens/Bookings";
import Checkout from "./screens/Checkout";
import Home from "./screens/Home";
import Menu from "./screens/Menu";
import ProgramDetail from "./screens/ProgramDetail";
import Programs from "./screens/Programs";
import Scan from "./screens/Scan";
import AdminHome from "./screens/admin/AdminHome";
import AdminMembers from "./screens/admin/AdminMembers";
import AdminProgramEdit from "./screens/admin/AdminProgramEdit";
import AdminPrograms from "./screens/admin/AdminPrograms";
import AdminSettings from "./screens/admin/AdminSettings";
import Profile from "./screens/Profile";
import Seats from "./screens/Seats";
import SignIn from "./screens/SignIn";
import Ticket from "./screens/Ticket";

function Welcome({ onChosen }: { onChosen: (locale: Locale) => void }) {
  const { t } = useTranslation();
  return (
    <main data-area="home" className="screen screen--full screen--welcome">
      <img className="welcome__logo" src="/photos/logo.png" alt="" />
      <h1 className="screen__title">{t("app.name")}</h1>
      {/* The only screen in the app that shows three languages at once, and
          it has to: a member cannot read an invitation written in a language
          they have not chosen yet. */}
      <p>Welcome / வரவேற்பு / Selamat Datang</p>
      <h2 className="section">{t("welcome.choose_language")}</h2>
      <div className="stack">
        {LOCALES.map((locale) => (
          <button
            key={locale}
            type="button"
            lang={locale}
            className="btn btn--primary"
            onClick={() => onChosen(locale)}
          >
            {LOCALE_NAMES[locale]}
          </button>
        ))}
      </div>
      <p className="fallback-note">{t("welcome.note")}</p>
    </main>
  );
}

export default function App() {
  const [locale, setChosen] = useState<Locale | null>(storedLocale());

  // The venue's wall clock comes from the server, so a committee that moves
  // an event to Bali changes one setting rather than shipping a build. The
  // defaults in format.ts cover the first paint and any offline start.
  useEffect(() => {
    api
      .meta()
      .then((meta) => setVenueZone(meta.venue_timezone, meta.venue_tz_label))
      .catch(() => undefined);
  }, []);

  const choose = (next: Locale) => {
    void setLocale(next);
    setChosen(next);
  };

  if (locale === null) return <Welcome onChosen={choose} />;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home locale={locale} />} />
        <Route path="/menu" element={<Menu />} />
        <Route path="/programs" element={<Programs locale={locale} />} />
        <Route path="/programs/:slug" element={<ProgramDetail locale={locale} />} />
        <Route path="/programs/:slug/seats" element={<Seats locale={locale} />} />
        <Route path="/checkout/:ref" element={<Checkout locale={locale} />} />
        <Route path="/tickets/:ref" element={<Ticket locale={locale} />} />
        <Route path="/signin" element={<SignIn locale={locale} />} />
        <Route path="/bookings" element={<Bookings locale={locale} />} />
        {/* Admin. The DOOR role is enforced by the API, not by this route --
            hiding a screen is a courtesy, refusing the call is the control. */}
        <Route path="/admin" element={<RequireAuth><AdminHome /></RequireAuth>} />
        <Route path="/admin/programs" element={<RequireAuth><AdminPrograms locale={locale} /></RequireAuth>} />
        <Route path="/admin/programs/:id" element={<RequireAuth><AdminProgramEdit locale={locale} /></RequireAuth>} />
        <Route path="/admin/members" element={<RequireAuth><AdminMembers /></RequireAuth>} />
        <Route path="/admin/settings" element={<RequireAuth><AdminSettings /></RequireAuth>} />
        <Route path="/admin/scan" element={<RequireAuth><Scan locale={locale} /></RequireAuth>} />
        <Route
          path="/profile"
          element={<Profile locale={locale} onLocaleChange={setChosen} />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
